import {
  CaretDownOutlined,
  CheckOutlined,
  CloseOutlined,
  CreditCardOutlined,
  DeleteOutlined,
  LoadingOutlined,
  LockOutlined,
  MinusOutlined,
  PlusOutlined,
  SafetyCertificateFilled,
  SafetyCertificateOutlined,
  SearchOutlined,
  StarOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  FeatureFlag,
  GetSubscriptionInfo200Response,
  SubscriptionDetails,
  SubscriptionScheduledChangeType,
  SubscriptionUpdateType,
  SubscriptionUser,
  UpdateSubscriptionsRequest,
  UserRoleType,
  UserSubscriptionInfo,
} from "@coeff/api";
import { useFeatureGate } from "@statsig/react-bindings";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { Dropdown, Input, Menu, message } from "antd";
import moment from "moment";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";

import { AVATAR_COLORS as avatarColors } from "../../../constants";
import { BContext } from "../../../types";
import {
  getActiveOtherUsersInDomain,
  getIsGmailLikeDomainFromEmail,
  isValidEmail,
  planNameFromType,
  sentryCapture,
  useApiContext,
  useTrack,
} from "../../../utils";
import { BillingContext } from "../Billing";
import { isPlanMember, sortUsers } from "../billing.utils";
import { AddSeatsModal } from "../components/AddSeatsModal";
import { AddUserToPlanModal } from "../components/AddUserToPlanModal";
import { BILLING_TOKENS as C } from "../components/designTokens";
import { HelpContent } from "../components/HelpContent";
import { DowngradeToFreeModal } from "../components/members/DowngradeToFreeModal";
import { ManageRoleModal } from "../components/members/ManageRoleModal";
import { RevokeInviteModal } from "../components/members/RevokeInviteModal";

import {
  Page,
  Content,
  MainCol,
  HeaderRow,
  Title,
  Subtitle,
  Controls,
  Search,
  FilterBtn,
  PrimaryBtn,
  OutlineBtn,
  GhostBtn,
  InfoBanner,
  PlanSummary,
  PlanRow,
  gridCols,
  ColHeader,
  GroupHeader,
  Row,
  UserCell,
  CheckBox,
  AvatarWrap,
  Avatar,
  DashedAvatar,
  NameCol,
  Pill,
  Badge,
  OutlineTag,
  PlanCol,
  LinkBtn,
  Kebab,
  RowMenu,
  EmptyState,
  BulkBar,
  Overlay,
  ModalCard,
  ModalHeader,
  RoleOption,
  WarnBox,
  ChipInput,
  Chip,
} from "./Members.styles";
import { PlanCard, assignableSeats, buildPlanCards, initials, inviteKey } from "./members.utils";

const PLAN_ORDER = ["enterprise", "custom", "pro", "plus", "starter"];

export const Members = () => {
  const track = useTrack();
  const history = useHistory();
  const { apiClient } = useApiContext();
  const { value: enableSelfServeAiDashboards } = useFeatureGate(
    FeatureFlag.EnableSelfServeAiDashboards
  );

  const {
    basePath,
    billingMetadata,
    userSubscription,
    subscriptions,
    updateSubscription,
    syncBilling,
    currentUserEmail,
    domain,
    domains_in_domain_family,
    isManager,
    enableSubscriptionUpdate,
    setContactSupportInfoBox,
    eventFrom,
  } = useContext(BillingContext) as BContext;

  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState<string>("all");
  const [busy, setBusy] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Invited users have no user_id yet, so they're selected by email.
  const [selectedInviteEmails, setSelectedInviteEmails] = useState<Set<string>>(new Set());

  const [roleModalUser, setRoleModalUser] = useState<UserSubscriptionInfo | null>(null);
  const [downgradeUser, setDowngradeUser] = useState<SubscriptionUser | null>(null);
  // Which subscription the pending downgrade targets (defaults to the loaded one).
  const [downgradeSubId, setDowngradeSubId] = useState<string | undefined>(undefined);
  // Pending-invite revoke target; subId scopes it to the invite's own subscription
  // (undefined = the loaded one).
  const [revokeTarget, setRevokeTarget] = useState<{ email: string; subId?: string } | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  // Which plan the invitees join; defaults to the loaded subscription.
  const [invitePlanSubId, setInvitePlanSubId] = useState<string | undefined>(undefined);
  const [inviteChips, setInviteChips] = useState<string[]>([]);
  const [inviteInput, setInviteInput] = useState("");
  // Add/Remove seats dialog, scoped to any plan card (subId undefined = loaded subscription).
  const [seatsModal, setSeatsModal] = useState<{ mode: "add" | "remove"; subId?: string } | null>(
    null
  );
  // One-shot buy-seats-and-assign flow (old "Add Users" modal): opened when assigning with no
  // free seats, pre-filled with the users being assigned.
  const [addUsersInitial, setAddUsersInitial] = useState<string[] | null>(null);

  const location = useLocation();
  useEffect(() => {
    // The link's `?from=` (e.g. "expansion_email_1" on the admin email's Manage Licenses link), so
    // analytics can tell which email drove the visit (ENG-8851), as billing_plan_chooser_viewed does.
    // A seatless admin gets there through /subscriptions, which drops the query string, so fall
    // back to the one Billing was opened with.
    track("billing_manage_team_viewed", {
      event_from: new URLSearchParams(location.search).get("from") ?? eventFrom,
    });
    document.title = "Members - Coefficient Workspace";
  }, []);

  const subscriptionDetails = userSubscription?.subscription_details;
  const ownerEmail = subscriptionDetails?.subscription_owner_email;

  const usersFromDomain = userSubscription?.users_from_domain ?? [];
  const inviteUsers = userSubscription?.invite_users ?? [];

  // Who can manage: workspace admins OR plan managers/owner. Everyone else gets a read-only
  // member view. (Plan managers were wrongly read-only because the page only checked isManager.)
  const meInfo = usersFromDomain.find(u => u.user.email === currentUserEmail);
  const iAmAdmin = meInfo?.user_role_type === UserRoleType.Admin;
  const iAmPlanManager =
    Boolean(isManager) || Boolean(meInfo?.is_plan_manager) || currentUserEmail === ownerEmail;
  const canManage =
    !getIsGmailLikeDomainFromEmail(currentUserEmail) && (iAmAdmin || iAmPlanManager);
  const readOnly = !canManage;

  const currentPlan = billingMetadata?.plans.find(
    p =>
      p.plan_type === subscriptionDetails?.plan_type &&
      p.billing_interval === subscriptionDetails?.billing_interval
  );
  const planCards = useMemo(
    () => buildPlanCards(subscriptions, subscriptionDetails),
    [subscriptions, subscriptionDetails]
  );

  // Workspace admins see and manage every plan; a plan owner/manager only sees the plan
  // they're part of (the loaded subscription).
  const visiblePlanCards = iAmAdmin
    ? planCards
    : planCards.filter(p => p.subscriptionId === subscriptionDetails?.subscription_id);

  const seated = usersFromDomain.filter(u => isPlanMember(u, subscriptionDetails));
  // On a plan somewhere in the domain family — not necessarily the loaded subscription.
  // isPlanMember() only matches members of the loaded subscription, so plan cards for
  // other subscriptions (e.g. a family domain's plan) must use this instead.
  const isOnAnyPlan = (u: UserSubscriptionInfo) => Boolean(u.managed_by_user);
  const seatsUsed = seated.length + inviteUsers.length;
  // assignableSeats() is the plan's seat count (max_users).
  const freeSeats = Math.max(
    (subscriptionDetails ? assignableSeats(subscriptionDetails) : 0) - seatsUsed,
    0
  );
  const currentTierLabel = planNameFromType(subscriptionDetails?.plan_type ?? "pro");

  // ---- Cross-plan management (admins) ----
  // The page's own payload covers only the loaded subscription, so for admins we also load
  // every other plan's subscription_info. Row actions then mutate the row's own subscription
  // in place instead of deep-linking to its page.
  const queryClient = useQueryClient();
  const otherPlanSubIds = iAmAdmin
    ? Array.from(
        new Set(
          planCards
            .map(p => p.subscriptionId)
            .filter(
              (id): id is string => Boolean(id) && id !== subscriptionDetails?.subscription_id
            )
        )
      )
    : [];
  const otherSubQueries = useQueries({
    queries: otherPlanSubIds.map(id => ({
      queryKey: ["MembersOtherSubInfo", id],
      queryFn: async () => (await apiClient.getSubscriptionInfo({ subscription_id: id })).data,
    })),
  });
  const otherSubs = otherSubQueries
    .map(q => q.data)
    .filter(Boolean) as GetSubscriptionInfo200Response[];

  type SubCtx = {
    details: SubscriptionDetails;
    users: UserSubscriptionInfo[];
    invites: UserSubscriptionInfo[];
    isLoadedSub: boolean;
  };
  const ctxBySubId = (subId?: string): SubCtx | null => {
    if (!subId) {
      return null;
    }
    if (subId === subscriptionDetails?.subscription_id && subscriptionDetails) {
      return {
        details: subscriptionDetails,
        users: usersFromDomain,
        invites: inviteUsers,
        isLoadedSub: true,
      };
    }
    const sub = otherSubs.find(s => s.subscription_details?.subscription_id === subId);
    return sub?.subscription_details
      ? {
          details: sub.subscription_details,
          users: sub.users_from_domain ?? [],
          invites: sub.invite_users ?? [],
          isLoadedSub: false,
        }
      : null;
  };
  // The subscription a row's seat belongs to (managed_by_user is that plan's payer).
  const subCtxFor = (info: UserSubscriptionInfo): SubCtx | null => {
    if (subscriptionDetails && isPlanMember(info, subscriptionDetails)) {
      return {
        details: subscriptionDetails,
        users: usersFromDomain,
        invites: inviteUsers,
        isLoadedSub: true,
      };
    }
    const payerEmail = info.managed_by_user?.email;
    if (!payerEmail) {
      return null;
    }
    const sub = otherSubs.find(
      s => s.subscription_details?.subscription_owner_email === payerEmail
    );
    return sub?.subscription_details
      ? {
          details: sub.subscription_details,
          users: sub.users_from_domain ?? [],
          invites: sub.invite_users ?? [],
          isLoadedSub: false,
        }
      : null;
  };
  const seatedIdsOf = (ctx: SubCtx): string[] =>
    ctx.users
      .filter(u => isPlanMember(u, ctx.details))
      .map(u => u.user.user_id)
      .filter((id): id is string => Boolean(id));
  const inviteEmailsOf = (ctx: SubCtx) => ctx.invites.map(u => u.user.email);
  // Attribute a member row to a plan card. Exact when the row's subscription info is loaded;
  // while another sub's info is still loading, bucket by tier under that tier's first card.
  const rowBelongsToCard = (
    u: UserSubscriptionInfo,
    card: { planType: string; subscriptionId?: string }
  ) => {
    const sid = subCtxFor(u)?.details.subscription_id;
    if (sid) {
      return sid === card.subscriptionId;
    }
    if (u.plan_type !== card.planType) {
      return false;
    }
    const firstOfTier = planCards.find(c => c.planType === card.planType);
    return firstOfTier?.subscriptionId === card.subscriptionId;
  };

  const adminCount = useMemo(
    () => usersFromDomain.filter(u => u.user_role_type === UserRoleType.Admin).length,
    [usersFromDomain]
  );
  const isLastAdmin = (info: UserSubscriptionInfo) =>
    adminCount <= 1 && info.user_role_type === UserRoleType.Admin;

  const matchesSearch = (u: UserSubscriptionInfo) => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return true;
    }
    const name = `${u.user.first_name ?? ""} ${u.user.last_name ?? ""}`.toLowerCase();
    return name.includes(q) || (u.user.email ?? "").toLowerCase().includes(q);
  };
  const matchesPlan = (u: UserSubscriptionInfo) => {
    if (planFilter === "all") {
      return true;
    }
    if (planFilter === "none") {
      return !u.managed_by_user;
    }
    return isOnAnyPlan(u) ? u.plan_type === planFilter : false;
  };

  const isAdminUser = (u: UserSubscriptionInfo) => u.user_role_type === UserRoleType.Admin;
  const admins = usersFromDomain.filter(isAdminUser).filter(matchesSearch).sort(sortUsers);
  const adminEmails = new Set(usersFromDomain.filter(isAdminUser).map(u => u.user.email));
  const seatedNonAdmin = usersFromDomain.filter(
    u => isOnAnyPlan(u) && !adminEmails.has(u.user.email)
  );
  const seatedPlanTypes = Array.from(
    new Set([
      ...seatedNonAdmin.map(u => u.plan_type).filter(Boolean),
      ...planCards.map(p => p.planType),
    ])
  ).sort((a, b) => PLAN_ORDER.indexOf(a as string) - PLAN_ORDER.indexOf(b as string));
  // Only recently-active users, matching the old Manage Licenses page — dormant accounts
  // shouldn't clutter the Unassigned section or the assign pickers.
  const noPlan = getActiveOtherUsersInDomain(
    usersFromDomain.filter(u => !u.managed_by_user && !adminEmails.has(u.user.email))
  )
    .filter(matchesSearch)
    .sort(sortUsers);
  const noSeatCandidates = getActiveOtherUsersInDomain(
    usersFromDomain.filter(u => !isOnAnyPlan(u))
  ).sort(sortUsers);

  const seatedUserIds = (): string[] =>
    seated.map(u => u.user.user_id).filter((id): id is string => Boolean(id));
  const inviteEmails = () => inviteUsers.map(u => u.user.email);

  const runUpdate = async (body: UpdateSubscriptionsRequest, msg: string, progressMsg?: string) => {
    const key = "members-update";
    setBusy(true);
    message.loading({ content: progressMsg ?? "Updating subscription…", key, duration: 0 });
    try {
      const res = await updateSubscription(body);
      if (!res.is_success) {
        message.error({
          content: res.error_msg || "Something went wrong. Please try again",
          key,
        });
        return false;
      }
      await syncBilling();
      await queryClient.invalidateQueries({ queryKey: ["MembersOtherSubInfo"] });
      message.success({ content: msg, key });
      return true;
    } finally {
      setBusy(false);
    }
  };
  const reassign = (ids: string[], invites: string[], msg: string, progressMsg?: string) =>
    runUpdate(
      {
        update_type: SubscriptionUpdateType.Reassignment,
        selected_user_ids: ids,
        invite_user_emails: invites,
        selected_data_source_types: subscriptionDetails?.selected_data_source_types,
        coupon_discount_percentage: billingMetadata?.coupon_discount_percentage,
        subscription_id: subscriptionDetails?.subscription_id,
      },
      msg,
      progressMsg
    );
  // Reassignment scoped to an arbitrary subscription (loaded or another plan the admin manages).
  const reassignOn = (
    ctx: SubCtx,
    ids: string[],
    invites: string[],
    msg: string,
    progressMsg?: string
  ) =>
    runUpdate(
      {
        update_type: SubscriptionUpdateType.Reassignment,
        selected_user_ids: ids,
        invite_user_emails: invites,
        selected_data_source_types: ctx.details.selected_data_source_types,
        coupon_discount_percentage: billingMetadata?.coupon_discount_percentage,
        subscription_id: ctx.details.subscription_id,
      },
      msg,
      progressMsg
    );

  const assignSeat = (user: SubscriptionUser) => {
    if (!user.user_id) {
      return;
    }
    if (freeSeats < 1) {
      // No free seats: buy the missing seat and assign this user in one confirmed purchase.
      setAddUsersInitial(user.email ? [user.email] : []);
      return;
    }
    reassign(
      [...seatedUserIds(), user.user_id],
      inviteEmails(),
      `${user.first_name || user.email} added to plan`,
      "Adding seat…"
    );
  };
  const removeSeat = async (user: SubscriptionUser, subId?: string) => {
    const ctx = ctxBySubId(subId ?? subscriptionDetails?.subscription_id);
    if (!ctx) {
      return;
    }
    await reassignOn(
      ctx,
      seatedIdsOf(ctx).filter(id => id !== user.user_id),
      inviteEmailsOf(ctx),
      `${user.first_name || user.email} downgraded to the Free plan`,
      "Removing from plan…"
    );
    track("billing_manage_team_user_removed", { removed_user: user.email });
  };
  const setPlanManager = (user: SubscriptionUser, make: boolean, subId?: string) =>
    runUpdate(
      {
        update_type: make
          ? SubscriptionUpdateType.AddPlanManager
          : SubscriptionUpdateType.RemovePlanManager,
        subscription_id: subId ?? subscriptionDetails?.subscription_id,
        new_plan_manager_user_id: user.user_id,
      },
      make ? "Plan manager added" : "Plan manager removed",
      "Updating plan manager…"
    );
  const saveRole = async (role: UserRoleType) => {
    if (!roleModalUser?.user.user_id) {
      return;
    }
    setBusy(true);
    try {
      await apiClient.updateUserMe({
        update_user_me_request: {
          user: { user_role_type: role, user_id: roleModalUser.user.user_id },
        },
      });
      await syncBilling();
      message.success("Role updated");
      setRoleModalUser(null);
    } catch (error) {
      sentryCapture({ error: error as Error, name: "MembersUpdateRole" });
      message.error("Failed to update role");
    } finally {
      setBusy(false);
    }
  };
  // Revoking = re-sending the invite list without this email, against the invite's own
  // subscription (other plans' invites must not be revoked via the loaded plan's list).
  const revokeInvite = (email: string, subId?: string) => {
    const ctx = ctxBySubId(subId ?? subscriptionDetails?.subscription_id);
    if (!ctx) {
      return Promise.resolve(false);
    }
    return reassignOn(
      ctx,
      seatedIdsOf(ctx),
      inviteEmailsOf(ctx).filter(e => e !== email),
      "Invite revoked · seat freed",
      "Removing from plan…"
    );
  };
  // The subscription the invite modal targets (plan picker, admins on multi-plan domains).
  const inviteCtx = ctxBySubId(invitePlanSubId ?? subscriptionDetails?.subscription_id);
  const inviteFreeSeats = inviteCtx
    ? Math.max(
        assignableSeats(inviteCtx.details) -
          seatedIdsOf(inviteCtx).length -
          inviteCtx.invites.length,
        0
      )
    : freeSeats;
  // Preselect the plan the invite targets: the card it was launched from, else the loaded one.
  const openInvite = (subId?: string) => {
    setInvitePlanSubId(subId ?? subscriptionDetails?.subscription_id);
    setShowInvite(true);
  };
  const sendInvites = async () => {
    if (!inviteCtx) {
      return;
    }
    const emails = [
      ...new Set([...inviteEmailsOf(inviteCtx), ...inviteChips.map(e => e.toLowerCase())]),
    ];
    const ok = await reassignOn(
      inviteCtx,
      seatedIdsOf(inviteCtx),
      emails,
      `${inviteChips.length} invite${inviteChips.length === 1 ? "" : "s"} sent`,
      "Sending invites…"
    );
    if (ok) {
      setShowInvite(false);
      setInviteChips([]);
      setInviteInput("");
      track("billing_manage_team_user_added", {
        event_from: "invite_user_popup",
        added_users: inviteChips,
      });
    }
  };
  // ---- Bulk ----
  const selMode = selectedIds.size > 0;
  const selMembers = usersFromDomain.filter(u => u.user.user_id && selectedIds.has(u.user.user_id));
  const selInviteEmails = inviteUsers
    .map(u => u.user.email)
    .filter(email => selectedIds.has(inviteKey(email)));
  const selSomeAdmin = selMembers.some(m => m.user_role_type === UserRoleType.Admin);
  const selSomeMember = selMembers.some(m => m.user_role_type !== UserRoleType.Admin);
  // Selected invites hold a seat, so they count for "Remove from plan".
  const selSomeSeat =
    selMembers.some(m => isPlanMember(m, subscriptionDetails)) || selInviteEmails.length > 0;
  const selSomeNoSeat = selMembers.some(m => !isOnAnyPlan(m));

  const bulkAssign = async () => {
    const ids = selMembers
      .filter(m => !isOnAnyPlan(m))
      .map(m => m.user.user_id)
      .filter((id): id is string => Boolean(id));
    if (ids.length > freeSeats) {
      // Not enough seats: hand the selection to the buy-and-assign flow.
      const emails = selMembers
        .filter(m => !isOnAnyPlan(m))
        .map(m => m.user.email)
        .filter(Boolean);
      setAddUsersInitial(emails);
      setSelectedIds(new Set());
      return;
    }
    const ok = await reassign(
      Array.from(new Set([...seatedUserIds(), ...ids])),
      inviteEmails(),
      `${ids.length} assigned to plan`,
      "Assigning seats…"
    );
    if (ok) {
      setSelectedIds(new Set());
    }
  };
  // Seats assigned on a given plan card (exact for the loaded subscription, approximate for others).
  // Seats assigned on a card, counted against that card's OWN subscription (two same-tier
  // plans must not merge). Falls back to tier-matching only while the other sub's info loads.
  const assignedOnPlan = (p: { planType: string; subscriptionId?: string }) => {
    if (p.subscriptionId === subscriptionDetails?.subscription_id) {
      return seated.length + inviteUsers.length;
    }
    const sub = otherSubs.find(s => s.subscription_details?.subscription_id === p.subscriptionId);
    if (sub?.subscription_details) {
      return (
        (sub.users_from_domain ?? []).filter(u => isPlanMember(u, sub.subscription_details))
          .length + (sub.invite_users?.length ?? 0)
      );
    }
    return usersFromDomain.filter(u => u.managed_by_user && u.plan_type === p.planType).length;
  };
  // Seats assignable on a card right now (max_users).
  const planCapacity = (p: { planType: string; subscriptionId?: string; seats: number }) => {
    const details =
      p.subscriptionId === subscriptionDetails?.subscription_id
        ? subscriptionDetails
        : otherSubs.find(s => s.subscription_details?.subscription_id === p.subscriptionId)
            ?.subscription_details;
    return details ? assignableSeats(details) : p.seats;
  };
  const bulkAssignToPlan = (p: { planType: string; subscriptionId?: string }) => {
    // We only have full member data for the loaded subscription; assigning to a different
    // subscription happens on that plan's own page.
    if (p.subscriptionId && p.subscriptionId !== subscriptionDetails?.subscription_id) {
      history.push({
        pathname: `${basePath}/subscription`,
        search: `?subscription_id=${p.subscriptionId}`,
      });
      return;
    }
    bulkAssign();
  };
  // Per-user plan picker for domains with multiple plans: assign on the loaded plan directly,
  // or jump to the other plan's members page (its data isn't loaded here).
  const assignSeatToPlan = (
    user: SubscriptionUser,
    p: { planType: string; subscriptionId?: string }
  ) => {
    if (p.subscriptionId && p.subscriptionId !== subscriptionDetails?.subscription_id) {
      const ctx = ctxBySubId(p.subscriptionId);
      // With the other plan's info loaded (admins), assign in place when it has a free seat.
      if (ctx && user.user_id) {
        const free = assignableSeats(ctx.details) - seatedIdsOf(ctx).length - ctx.invites.length;
        if (free >= 1) {
          reassignOn(
            ctx,
            [...seatedIdsOf(ctx), user.user_id],
            inviteEmailsOf(ctx),
            `${user.first_name || user.email} added to ${planNameFromType(p.planType)} plan`
          );
          return;
        }
        // No free seats: buy them on that plan's own page.
      }
      history.push({
        pathname: `${basePath}/team/manage`,
        search: `?subscription_id=${p.subscriptionId}`,
      });
      return;
    }
    assignSeat(user);
  };
  const assignSeatMenu = (user: SubscriptionUser) => (
    <RowMenu>
      {planCards.map(p => {
        const free = planCapacity(p) - assignedOnPlan(p);
        return (
          <Menu.Item key={p.subscriptionId || p.planType} onClick={() => assignSeatToPlan(user, p)}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
              }}
            >
              {planBadge(p.planType, p.label)}
              <span style={{ color: free > 0 ? C.placeholder : C.pendingFg, fontSize: 12 }}>
                {free > 0 ? `${free} free` : "Buy a seat"}
              </span>
            </div>
          </Menu.Item>
        );
      })}
    </RowMenu>
  );
  const bulkRemoveFromPlan = async () => {
    const ids = new Set(selectedIds);
    // Removing a selected pending invite from the plan = revoking it (frees the reserved seat).
    const revoked = new Set(selInviteEmails);
    const ok = await reassign(
      seatedUserIds().filter(id => !ids.has(id)),
      inviteEmails().filter(e => !revoked.has(e)),
      "Removed selected from plan",
      "Removing from plan…"
    );
    if (ok) {
      setSelectedIds(new Set());
    }
  };
  const bulkSetRole = async (role: UserRoleType) => {
    const targets = selMembers.filter(m => m.user.user_id && m.user_role_type !== role);
    if (targets.length === 0) {
      message.info(
        role === UserRoleType.Admin
          ? "Everyone selected is already an admin"
          : "Everyone selected is already a member"
      );
      return;
    }
    if (role === UserRoleType.Member) {
      const remaining =
        adminCount - targets.filter(m => m.user_role_type === UserRoleType.Admin).length;
      if (remaining < 1) {
        message.error("Blocked — your domain must have at least one admin");
        return;
      }
    }
    setBusy(true);
    try {
      for (const info of targets) {
        await apiClient.updateUserMe({
          update_user_me_request: { user: { user_role_type: role, user_id: info.user.user_id } },
        });
      }
      await syncBilling();
      message.success(
        role === UserRoleType.Admin ? "Selected are now admins" : "Selected are now members"
      );
      setSelectedIds(new Set());
    } catch (error) {
      sentryCapture({ error: error as Error, name: "MembersBulkRole" });
      message.error("Failed to update roles");
    } finally {
      setBusy(false);
    }
  };
  const toggleSelected = (id: string) =>
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  const selectableIds = [
    ...usersFromDomain.filter(u => u.user.user_id).map(u => u.user.user_id as string),
    ...inviteUsers.map(u => inviteKey(u.user.email)),
  ];
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selectedIds.has(id));
  const toggleSelectAll = () => setSelectedIds(allSelected ? new Set() : new Set(selectableIds));

  const openRoleModal = (info: UserSubscriptionInfo) => {
    setRoleModalUser(info);
  };

  // ---- render helpers ----
  // label overrides the tier name with the numbered card label ("Pro 1") wherever a row
  // refers to one specific subscription of a tier that appears multiple times.
  const planBadge = (planType?: string, label?: string) =>
    planType === "starter" || planType === "plus" ? (
      <Badge bg={C.starterBg} fg={C.starterFg}>
        {label ?? "Starter"}
      </Badge>
    ) : (
      <Badge bg={C.proBg} fg={C.proFg}>
        {label ??
          (planType === "enterprise" ? "Enterprise" : planType === "custom" ? "Custom" : "Pro")}
      </Badge>
    );
  const rolePill = (info: UserSubscriptionInfo) =>
    info.user_role_type === UserRoleType.Admin ? (
      <Pill bg={C.blue50} fg={C.blue}>
        <SafetyCertificateFilled />
        Admin
      </Pill>
    ) : null;
  const billingTag = (info: UserSubscriptionInfo) => {
    // Owner: payer of the plan the user belongs to (managed_by_user is that plan's payer),
    // so this works across every plan in the domain, not just the loaded subscription.
    if (
      info.user.email === ownerEmail ||
      (info.managed_by_user && info.user.email === info.managed_by_user.email)
    ) {
      return <OutlineTag>Manager</OutlineTag>;
    }
    // Manager: the loaded payload's flag covers the loaded plan; for rows on another plan,
    // read the flag from that plan's lazily-fetched info (is_plan_manager is per-subscription).
    const ctx = subCtxFor(info);
    const isMgr = ctx
      ? ctx.isLoadedSub
        ? Boolean(info.is_plan_manager)
        : Boolean(ctx.users.find(u => u.user.user_id === info.user.user_id)?.is_plan_manager)
      : Boolean(info.is_plan_manager);
    if (isMgr) {
      return <OutlineTag>Manager</OutlineTag>;
    }
    return null;
  };

  const rowMenu = (info: UserSubscriptionInfo) => {
    const user = info.user;
    const isAdmin = info.user_role_type === UserRoleType.Admin;
    // The subscription this row's seat belongs to — the loaded plan, or another plan whose
    // info was lazily fetched (admins), so every plan is manageable in place.
    const rowCtx = subCtxFor(info);
    const hasSeat = Boolean(rowCtx);
    const onOtherPlan = !hasSeat && isOnAnyPlan(info);
    const rowSubId = rowCtx?.details.subscription_id;
    const isOwner = user.email === rowCtx?.details.subscription_owner_email;
    const isMgr =
      isOwner ||
      (rowCtx?.isLoadedSub
        ? Boolean(info.is_plan_manager)
        : Boolean(rowCtx?.users.find(u => u.user.user_id === user.user_id)?.is_plan_manager));
    const lastAdmin = isLastAdmin(info);
    return (
      <RowMenu>
        {/* Workspace role management is admin-only; plan managers only manage their plan. */}
        {iAmAdmin && (
          <Menu.ItemGroup title="WORKSPACE ROLE">
            {lastAdmin ? (
              <Menu.Item key="role" disabled icon={<LockOutlined />}>
                <div style={{ lineHeight: 1.3 }}>
                  Remove admin access
                  <div style={{ fontSize: 11.5, color: C.faint }}>
                    Last admin — promote someone first
                  </div>
                </div>
              </Menu.Item>
            ) : (
              <Menu.Item
                key="role"
                icon={<SafetyCertificateOutlined />}
                onClick={() => openRoleModal(info)}
              >
                {isAdmin ? "Remove admin access" : "Make admin"}
              </Menu.Item>
            )}
          </Menu.ItemGroup>
        )}
        {hasSeat && (
          <Menu.ItemGroup title="BILLING ROLE">
            {isOwner ? (
              <Menu.Item key="owner" disabled icon={<LockOutlined />}>
                Plan owner — can't be changed
              </Menu.Item>
            ) : isMgr ? (
              <Menu.Item
                key="rm-mgr"
                icon={<StarOutlined />}
                onClick={() => setPlanManager(user, false, rowSubId)}
              >
                Remove as plan manager
              </Menu.Item>
            ) : (
              <Menu.Item
                key="mk-mgr"
                icon={<StarOutlined />}
                onClick={() => setPlanManager(user, true, rowSubId)}
              >
                Make plan manager
              </Menu.Item>
            )}
          </Menu.ItemGroup>
        )}
        <Menu.ItemGroup title="SEAT / PLAN">
          {hasSeat ? (
            <Menu.Item
              key="rm-plan"
              icon={<MinusOutlined />}
              onClick={() => {
                setDowngradeSubId(rowSubId);
                setDowngradeUser(user);
              }}
            >
              Remove from plan
            </Menu.Item>
          ) : onOtherPlan ? (
            iAmAdmin &&
            planCards.find(
              p =>
                p.planType === info.plan_type &&
                p.subscriptionId &&
                p.subscriptionId !== subscriptionDetails?.subscription_id
            ) ? (
              <Menu.Item
                key="other-plan"
                icon={<CreditCardOutlined />}
                onClick={() =>
                  history.push({
                    pathname: `${basePath}/team/manage`,
                    search: `?subscription_id=${
                      planCards.find(p => p.planType === info.plan_type)?.subscriptionId
                    }`,
                  })
                }
              >
                <div style={{ lineHeight: 1.3 }}>
                  Manage on {planNameFromType(info.plan_type ?? "")} plan
                  <div style={{ fontSize: 11.5, color: C.placeholder }}>
                    Opens that plan's members view
                  </div>
                </div>
              </Menu.Item>
            ) : (
              <Menu.Item key="other-plan" disabled icon={<LockOutlined />}>
                <div style={{ lineHeight: 1.3 }}>
                  On another plan
                  <div style={{ fontSize: 11.5, color: C.faint }}>
                    Managed by{" "}
                    {info.managed_by_user?.first_name ||
                      info.managed_by_user?.email ||
                      "another user"}
                  </div>
                </div>
              </Menu.Item>
            )
          ) : planCards.length > 1 ? (
            planCards.map(p => {
              const free = planCapacity(p) - assignedOnPlan(p);
              return (
                <Menu.Item
                  key={`assign-${p.subscriptionId || p.planType}`}
                  icon={<CreditCardOutlined />}
                  onClick={() => assignSeatToPlan(user, p)}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                    <span>Assign {p.label} seat</span>
                    <span style={{ color: C.placeholder, fontSize: 11.5 }}>
                      {free > 0 ? `${free} free` : "Buy a seat"}
                    </span>
                  </div>
                </Menu.Item>
              );
            })
          ) : (
            <Menu.Item key="assign" icon={<CreditCardOutlined />} onClick={() => assignSeat(user)}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                <span>Assign {currentTierLabel} seat</span>
                <span style={{ color: C.placeholder, fontSize: 11.5 }}>
                  {freeSeats > 0 ? `${freeSeats} free` : "Buy a seat"}
                </span>
              </div>
            </Menu.Item>
          )}
        </Menu.ItemGroup>
      </RowMenu>
    );
  };

  const memberRow = (info: UserSubscriptionInfo, i: number) => {
    const user = info.user;
    const name = `${user.first_name || user.email} ${user.last_name || ""}`.trim();
    const id = user.user_id || "";
    const sel = selectedIds.has(id);
    const selectable = !readOnly && Boolean(id);
    return (
      <Row key={id || user.email || i} selected={sel} className={selectable ? "selRow" : undefined}>
        <UserCell>
          <AvatarWrap
            selectable={selectable}
            sel={sel}
            selMode={selMode}
            onClick={() => selectable && toggleSelected(id)}
          >
            <span className="av">
              <Avatar bg={avatarColors[i % avatarColors.length]}>{initials(name)}</Avatar>
            </span>
            {selectable && (
              <span className="ck">
                <CheckBox checked={sel}>{sel && <CheckOutlined />}</CheckBox>
              </span>
            )}
          </AvatarWrap>
          <NameCol>
            <div className="name" title={name}>
              {name}
            </div>
            <div className="email" title={user.email}>
              {user.email}
            </div>
          </NameCol>
        </UserCell>
        <div>{rolePill(info)}</div>
        <PlanCol>
          {isOnAnyPlan(info) ? (
            planBadge(info.plan_type ?? subscriptionDetails?.plan_type)
          ) : readOnly ? (
            <span style={{ color: C.placeholder }}>—</span>
          ) : planCards.length > 1 ? (
            <Dropdown overlay={assignSeatMenu(user)} trigger={["click"]} placement="bottomLeft">
              <LinkBtn disabled={busy}>Assign seat</LinkBtn>
            </Dropdown>
          ) : (
            <LinkBtn disabled={busy} onClick={() => assignSeat(user)}>
              Assign seat
            </LinkBtn>
          )}
          {billingTag(info)}
        </PlanCol>
        <div style={{ textAlign: "right" }}>
          {!readOnly && (
            <Dropdown overlay={rowMenu(info)} trigger={["click"]} placement="bottomRight">
              <Kebab aria-label="Row actions">⋯</Kebab>
            </Dropdown>
          )}
        </div>
      </Row>
    );
  };

  const inviteRow = (info: UserSubscriptionInfo, i: number, cardSubId?: string) => {
    const email = info.user.email;
    const name = `${info.user.first_name || email} ${info.user.last_name || ""}`.trim();
    const key = inviteKey(email);
    const sel = selectedIds.has(key);
    // Bulk actions only operate on the loaded subscription, so other plans' invites are
    // shown but not selectable — their kebab still revokes against their own plan.
    const isLoadedInvite = !cardSubId || cardSubId === subscriptionDetails?.subscription_id;
    const selectable = !readOnly && Boolean(email) && isLoadedInvite;
    return (
      <Row key={`inv-${email || i}`} selected={sel} className={selectable ? "selRow" : undefined}>
        <UserCell>
          <AvatarWrap
            selectable={selectable}
            sel={sel}
            selMode={selMode}
            onClick={() => selectable && toggleSelected(key)}
          >
            <span className="av">
              <Avatar bg={avatarColors[(i + 3) % avatarColors.length]}>{initials(name)}</Avatar>
            </span>
            {selectable && (
              <span className="ck">
                <CheckBox checked={sel}>{sel && <CheckOutlined />}</CheckBox>
              </span>
            )}
          </AvatarWrap>
          <NameCol>
            <div className="name" title={name}>
              {name}
            </div>
            <div className="email" title={email}>
              {email}
            </div>
          </NameCol>
        </UserCell>
        <div />
        <PlanCol>{planBadge(info.plan_type ?? subscriptionDetails?.plan_type)}</PlanCol>
        <div style={{ textAlign: "right" }}>
          {!readOnly && (
            <Dropdown
              trigger={["click"]}
              placement="bottomRight"
              overlay={
                <RowMenu>
                  <Menu.Item
                    key="revoke"
                    danger
                    icon={<MinusOutlined />}
                    onClick={() => setRevokeTarget({ email, subId: cardSubId })}
                  >
                    Remove from plan
                  </Menu.Item>
                </RowMenu>
              }
            >
              <Kebab aria-label="Invite actions">⋯</Kebab>
            </Dropdown>
          )}
        </div>
      </Row>
    );
  };

  const filterMenu = (
    <Menu onClick={({ key }) => setPlanFilter(String(key))}>
      <Menu.Item key="all">All plans</Menu.Item>
      {seatedPlanTypes.map(pt => (
        <Menu.Item key={pt as string}>{planNameFromType((pt as string) ?? "")}</Menu.Item>
      ))}
      <Menu.Item key="none">No plan</Menu.Item>
    </Menu>
  );
  const filterLabel =
    planFilter === "all"
      ? "All plans"
      : planFilter === "none"
      ? "No plan"
      : planNameFromType(planFilter);

  const validateInviteEmail = (email: string): string | undefined => {
    if (!isValidEmail(email)) {
      return "Not a valid email";
    }
    if (!domains_in_domain_family.includes(email.split("@")[1])) {
      return "Not on your domain";
    }
    return undefined;
  };
  const addInviteChip = (raw: string) => {
    const email = raw.trim().toLowerCase();
    const alreadyInvited = inviteCtx ? inviteEmailsOf(inviteCtx) : inviteEmails();
    if (!email || inviteChips.includes(email) || alreadyInvited.includes(email)) {
      setInviteInput("");
      return;
    }
    setInviteChips(prev => [...prev, email]);
    setInviteInput("");
  };
  const inviteHasError = inviteChips.some(e => validateInviteEmail(e));

  const anyMembers =
    admins.length > 0 ||
    seatedNonAdmin.filter(matchesSearch).filter(matchesPlan).length > 0 ||
    noPlan.filter(matchesPlan).length > 0 ||
    inviteUsers.length > 0;

  const assignMenuFor = (card: PlanCard) => (
    <RowMenu>
      <Menu.ItemGroup title="ASSIGN TO">
        {noSeatCandidates.length === 0 ? (
          <Menu.Item key="none" disabled>
            Everyone already holds a license
          </Menu.Item>
        ) : (
          noSeatCandidates.slice(0, 8).map(u => (
            <Menu.Item
              key={u.user.user_id || u.user.email}
              onClick={() =>
                card.subscriptionId === subscriptionDetails?.subscription_id
                  ? assignSeat(u.user)
                  : assignSeatToPlan(u.user, card)
              }
            >
              {`${u.user.first_name || u.user.email} ${u.user.last_name || ""}`.trim()}
            </Menu.Item>
          ))
        )}
        <Menu.Divider />
        <Menu.Item key="invite-new" onClick={() => openInvite(card.subscriptionId)}>
          + Invite someone new
        </Menu.Item>
      </Menu.ItemGroup>
    </RowMenu>
  );

  const unassignedRow = (card: PlanCard, n: number) => (
    <Row key={`unassigned-${card.subscriptionId || card.planType}`}>
      <UserCell>
        <DashedAvatar>
          <UserOutlined />
        </DashedAvatar>
        <NameCol>
          <div className="name">
            {n} Unassigned License{n === 1 ? "" : "s"}
          </div>
        </NameCol>
      </UserCell>
      <div style={{ color: C.placeholder }}>—</div>
      <div>
        {!readOnly && (
          <Dropdown overlay={assignMenuFor(card)} trigger={["click"]} placement="bottomLeft">
            <LinkBtn>Assign license</LinkBtn>
          </Dropdown>
        )}
      </div>
      <div />
    </Row>
  );

  return (
    <Page>
      <HeaderRow>
        <div>
          <Title>Members</Title>
        </div>

        <Controls>
          <Search
            allowClear
            prefix={<SearchOutlined style={{ color: C.placeholder }} />}
            placeholder="Search members"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <Dropdown overlay={filterMenu} trigger={["click"]} placement="bottomRight">
            <FilterBtn>
              {filterLabel}
              <CaretDownOutlined style={{ fontSize: 11, color: C.muted }} />
            </FilterBtn>
          </Dropdown>
          {!readOnly && (
            <PrimaryBtn onClick={() => openInvite()}>
              <PlusOutlined style={{ fontSize: 12 }} />
              Invite members
            </PrimaryBtn>
          )}
        </Controls>
      </HeaderRow>

      <Content>
        <MainCol>
          {!readOnly && meInfo && !isOnAnyPlan(meInfo) && (
            <InfoBanner>
              <div className="t">You're on the Free plan</div>
              <div className="s">Assign yourself a seat to get the most out of Coefficient.</div>
            </InfoBanner>
          )}
          {visiblePlanCards.length > 0 && (
            <PlanSummary>
              {visiblePlanCards.map((p, i) => {
                const isLoadedCard = p.subscriptionId === subscriptionDetails?.subscription_id;
                // Seat removals are scheduled at renewal (max_users doesn't drop yet) —
                // surface the pending change so a confirmed removal is visible immediately.
                const cardDetails = isLoadedCard
                  ? subscriptionDetails
                  : otherSubs.find(
                      s => s.subscription_details?.subscription_id === p.subscriptionId
                    )?.subscription_details;
                const scheduledSeats =
                  cardDetails?.has_scheduled_changes &&
                  cardDetails.scheduled_change_type ===
                    SubscriptionScheduledChangeType.DowngradeNumLicenses
                    ? cardDetails.scheduled_changes_info?.num_licenses
                    : undefined;
                const openSeats = (seatsMode: "add" | "remove") => {
                  // Self-serve plans only (same rule as the old Manage Licenses menu):
                  // sales-managed plans route to support instead. If the card's data is
                  // still loading we open the dialog anyway — it shows a spinner and does
                  // its own contact-us check once the plan resolves.
                  const selfServe = isLoadedCard
                    ? enableSubscriptionUpdate
                    : !cardDetails || cardDetails.checkout_mode !== "contact_us";
                  if (!selfServe) {
                    setContactSupportInfoBox({ title: "Manage Licenses" });
                    return;
                  }
                  setSeatsModal({
                    mode: seatsMode,
                    subId: isLoadedCard ? undefined : p.subscriptionId,
                  });
                };
                return (
                  <PlanRow key={p.subscriptionId || i}>
                    {planBadge(p.planType, p.label)}
                    <div className="seats">
                      <b>{p.seats}</b> seats
                      {scheduledSeats !== undefined && (
                        <span style={{ color: C.pendingFg, fontSize: 12, marginLeft: 8 }}>
                          → {scheduledSeats} at renewal
                        </span>
                      )}
                    </div>
                    {!readOnly && (
                      <>
                        <OutlineBtn
                          onClick={() =>
                            history.push({
                              pathname: `${basePath}/subscription`,
                              search: p.subscriptionId
                                ? `?subscription_id=${p.subscriptionId}`
                                : window.location.search,
                            })
                          }
                        >
                          Manage plan
                        </OutlineBtn>
                        <OutlineBtn onClick={() => openSeats("add")}>Add seats</OutlineBtn>
                      </>
                    )}
                  </PlanRow>
                );
              })}
            </PlanSummary>
          )}

          <ColHeader>
            <div className="userCol">
              {!readOnly && selMode && (
                <CheckBox
                  checked={allSelected}
                  indeterminate={!allSelected}
                  onClick={toggleSelectAll}
                >
                  {allSelected ? (
                    <CheckOutlined />
                  ) : (
                    <span style={{ width: 10, height: 2.5, background: "#fff", borderRadius: 1 }} />
                  )}
                </CheckBox>
              )}
              <span className="c">User</span>
            </div>
            <div className="c">Role</div>
            <div className="c">Plan</div>
            <div className="c" />
          </ColHeader>

          {iAmAdmin && admins.length > 0 && (
            <>
              <GroupHeader>
                <span className="t">Admins</span>
                <span className="m">Manage the workspace and billing</span>
              </GroupHeader>
              {admins.map((u, i) => memberRow(u, i))}
            </>
          )}

          {visiblePlanCards.map(card => {
            // One section per subscription (not per tier) so multiple same-tier plans don't
            // merge; card.label disambiguates them (Pro 1, Pro 2, … by purchase date).
            const rowsForCard = seatedNonAdmin
              .filter(u => rowBelongsToCard(u, card))
              .filter(matchesSearch)
              .filter(() => planFilter === "all" || planFilter === card.planType)
              .sort(sortUsers);
            const isLoadedCard = card.subscriptionId === subscriptionDetails?.subscription_id;
            // Pending invites reserve seats on their own subscription; other plans' invites
            // come from that plan's lazily-fetched info (admins), not the loaded payload.
            const cardInvites = isLoadedCard
              ? inviteUsers
              : otherSubs.find(s => s.subscription_details?.subscription_id === card.subscriptionId)
                  ?.invite_users ?? [];
            const invitesForCard = cardInvites
              .filter(() => planFilter === "all" || planFilter === card.planType)
              .filter(matchesSearch);
            const total = card.seats;
            const assignedAll =
              usersFromDomain.filter(u => isOnAnyPlan(u) && rowBelongsToCard(u, card)).length +
              cardInvites.length;
            // Unassigned = assignable capacity minus holders; capacity honors a pending
            // enforced license reduction (effective count), not the contract max.
            const unassigned = Math.max(planCapacity(card) - assignedAll, 0);
            const showUnassigned =
              !readOnly &&
              !search.trim() &&
              (planFilter === "all" || planFilter === card.planType) &&
              unassigned > 0;
            if (rowsForCard.length === 0 && invitesForCard.length === 0 && !showUnassigned) {
              return null;
            }
            return (
              <React.Fragment key={`plan-${card.subscriptionId || card.planType}`}>
                <GroupHeader>
                  <span className="t">{card.label} plan</span>
                  <span className="m">
                    {total} seats · {assignedAll} assigned
                  </span>
                </GroupHeader>
                {rowsForCard.map((u, i) => memberRow(u, i + 100))}
                {invitesForCard.map((u, i) =>
                  inviteRow(u, i, isLoadedCard ? undefined : card.subscriptionId)
                )}
                {showUnassigned && unassignedRow(card, unassigned)}
              </React.Fragment>
            );
          })}

          {!readOnly &&
            noPlan.filter(matchesPlan).length > 0 &&
            (planFilter === "all" || planFilter === "none") && (
              <>
                <GroupHeader>
                  <span className="t">Unassigned</span>
                  <span className="m">No license assigned</span>
                </GroupHeader>
                {noPlan.map((u, i) => memberRow(u, i + 500))}
              </>
            )}

          {!anyMembers && (
            <EmptyState>
              <div className="t">No members match this view</div>
              <div className="s">Try a different search or plan filter.</div>
            </EmptyState>
          )}

          {/* Bulk action bar (context-aware) */}
          {selMode && (
            <BulkBar>
              <span className="count">{selectedIds.size} selected</span>
              <span className="sep" />
              {selSomeNoSeat &&
                (planCards.length > 1 ? (
                  <Dropdown
                    trigger={["click"]}
                    placement="top"
                    overlay={
                      <RowMenu>
                        {planCards.map(p => {
                          const free = planCapacity(p) - assignedOnPlan(p);
                          return (
                            <Menu.Item
                              key={p.subscriptionId || p.planType}
                              onClick={() => bulkAssignToPlan(p)}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 16,
                                }}
                              >
                                {planBadge(p.planType, p.label)}
                                <span
                                  style={{
                                    color: free > 0 ? C.placeholder : C.pendingFg,
                                    fontSize: 12,
                                  }}
                                >
                                  {free > 0 ? `${free} free` : `${Math.abs(free)} more needed`}
                                </span>
                              </div>
                            </Menu.Item>
                          );
                        })}
                      </RowMenu>
                    }
                  >
                    <button className="act">
                      Assign to plan <CaretDownOutlined style={{ fontSize: 10 }} />
                    </button>
                  </Dropdown>
                ) : (
                  <button className="act" onClick={bulkAssign} disabled={busy}>
                    Assign {currentTierLabel} seats
                  </button>
                ))}
              {selSomeSeat && (
                <button className="act" onClick={bulkRemoveFromPlan} disabled={busy}>
                  Remove from plan
                </button>
              )}
              {iAmAdmin && selMembers.length > 0 && (
                <>
                  <span className="sep" />
                  {selSomeAdmin && selSomeMember ? (
                    <Dropdown
                      trigger={["click"]}
                      placement="top"
                      overlay={
                        <Menu>
                          <Menu.Item key="a" onClick={() => bulkSetRole(UserRoleType.Admin)}>
                            Make admin
                          </Menu.Item>
                          <Menu.Item key="m" onClick={() => bulkSetRole(UserRoleType.Member)}>
                            Make member
                          </Menu.Item>
                        </Menu>
                      }
                    >
                      <button className="act">
                        Change role <CaretDownOutlined style={{ fontSize: 10 }} />
                      </button>
                    </Dropdown>
                  ) : selSomeMember ? (
                    <button
                      className="act"
                      onClick={() => bulkSetRole(UserRoleType.Admin)}
                      disabled={busy}
                    >
                      Make admin
                    </button>
                  ) : (
                    <button
                      className="act"
                      onClick={() => bulkSetRole(UserRoleType.Member)}
                      disabled={busy}
                    >
                      Make member
                    </button>
                  )}
                </>
              )}
              <button
                className="act x"
                onClick={() => setSelectedIds(new Set())}
                aria-label="Clear selection"
              >
                <CloseOutlined />
              </button>
            </BulkBar>
          )}
        </MainCol>
        <HelpContent />
      </Content>

      <ManageRoleModal
        user={
          roleModalUser
            ? {
                name: `${roleModalUser.user.first_name || roleModalUser.user.email} ${
                  roleModalUser.user.last_name || ""
                }`.trim(),
                isAdmin: roleModalUser.user_role_type === UserRoleType.Admin,
                isLastAdmin: adminCount <= 1 && roleModalUser.user_role_type === UserRoleType.Admin,
              }
            : null
        }
        busy={busy}
        onSave={saveRole}
        onClose={() => setRoleModalUser(null)}
      />

      <DowngradeToFreeModal
        user={downgradeUser}
        busy={busy}
        hasWebDashboardAddon={
          enableSelfServeAiDashboards && Boolean(subscriptionDetails?.has_web_dashboard_addon)
        }
        onConfirm={async () => {
          if (downgradeUser) {
            await removeSeat(downgradeUser, downgradeSubId);
          }
          setDowngradeUser(null);
        }}
        onClose={() => setDowngradeUser(null)}
      />

      <RevokeInviteModal
        email={revokeTarget?.email ?? null}
        busy={busy}
        onConfirm={async () => {
          if (revokeTarget) {
            await revokeInvite(revokeTarget.email, revokeTarget.subId);
          }
          setRevokeTarget(null);
        }}
        onClose={() => setRevokeTarget(null)}
      />

      {/* Invite members modal */}
      {showInvite && (
        <Overlay onClick={() => setShowInvite(false)}>
          <ModalCard onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <div>
                <div className="title">Invite members</div>
                <div className="sub">Invitees must be on your domain — {domain}</div>
              </div>
              <button className="close" onClick={() => setShowInvite(false)}>
                <CloseOutlined />
              </button>
            </ModalHeader>
            {visiblePlanCards.length > 1 && (
              <div style={{ margin: "14px 0 2px" }}>
                <div
                  style={{ fontSize: 12, fontWeight: 600, color: C.placeholder, marginBottom: 6 }}
                >
                  INVITE TO
                </div>
                <Dropdown
                  trigger={["click"]}
                  placement="bottomLeft"
                  overlay={
                    <RowMenu>
                      {visiblePlanCards.map(p => {
                        const ctx = ctxBySubId(p.subscriptionId);
                        const free = ctx
                          ? Math.max(
                              assignableSeats(ctx.details) -
                                seatedIdsOf(ctx).length -
                                ctx.invites.length,
                              0
                            )
                          : undefined;
                        return (
                          <Menu.Item
                            key={p.subscriptionId || p.planType}
                            disabled={!ctx}
                            onClick={() => setInvitePlanSubId(p.subscriptionId)}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 16,
                              }}
                            >
                              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                {planBadge(p.planType, p.label)}
                              </span>
                              <span style={{ color: C.placeholder, fontSize: 12 }}>
                                {ctx ? `${free} free` : "loading…"}
                              </span>
                            </div>
                          </Menu.Item>
                        );
                      })}
                    </RowMenu>
                  }
                >
                  <FilterBtn style={{ height: 36 }}>
                    {(() => {
                      const sel =
                        visiblePlanCards.find(
                          p =>
                            p.subscriptionId ===
                            (invitePlanSubId ?? subscriptionDetails?.subscription_id)
                        ) ?? visiblePlanCards[0];
                      return (
                        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          {planBadge(sel.planType, sel.label)}
                        </span>
                      );
                    })()}
                    <CaretDownOutlined style={{ fontSize: 11, color: C.muted }} />
                  </FilterBtn>
                </Dropdown>
              </div>
            )}
            <div style={{ marginTop: 16 }}>
              <ChipInput onClick={() => document.getElementById("invite-input")?.focus()}>
                {inviteChips.map(email => {
                  const bad = Boolean(validateInviteEmail(email));
                  return (
                    <Chip key={email} bad={bad} title={validateInviteEmail(email)}>
                      {email}
                      <span
                        className="x"
                        onClick={() => setInviteChips(prev => prev.filter(e => e !== email))}
                      >
                        <CloseOutlined />
                      </span>
                    </Chip>
                  );
                })}
                <input
                  id="invite-input"
                  value={inviteInput}
                  placeholder={inviteChips.length ? "" : "Add an address and press Enter…"}
                  onChange={e => setInviteInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      addInviteChip(inviteInput);
                    } else if (e.key === "Backspace" && !inviteInput && inviteChips.length) {
                      setInviteChips(prev => prev.slice(0, -1));
                    }
                  }}
                  onBlur={() => addInviteChip(inviteInput)}
                />
              </ChipInput>
              {inviteChips.length > 0 && (
                <div
                  style={{
                    marginTop: 12,
                    fontSize: 13,
                    color: inviteChips.length > inviteFreeSeats ? C.danger : C.muted,
                  }}
                >
                  {inviteChips.length > inviteFreeSeats
                    ? `Only ${inviteFreeSeats} seat${
                        inviteFreeSeats === 1 ? "" : "s"
                      } available on this plan — remove ${
                        inviteChips.length - inviteFreeSeats
                      } invitee${
                        inviteChips.length - inviteFreeSeats === 1 ? "" : "s"
                      } or add seats first.`
                    : `${inviteChips.length} invitee${
                        inviteChips.length === 1 ? "" : "s"
                      } will each reserve a seat when you send — ${inviteFreeSeats} seat${
                        inviteFreeSeats === 1 ? "" : "s"
                      } available.`}
                </div>
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}>
              <GhostBtn onClick={() => setShowInvite(false)}>Cancel</GhostBtn>
              <PrimaryBtn
                disabled={
                  busy ||
                  inviteChips.length === 0 ||
                  inviteHasError ||
                  inviteChips.length > inviteFreeSeats
                }
                onClick={sendInvites}
              >
                {busy && <LoadingOutlined spin />}
                Send {inviteChips.length || ""} invite{inviteChips.length === 1 ? "" : "s"}
              </PrimaryBtn>
            </div>
          </ModalCard>
        </Overlay>
      )}

      <AddSeatsModal
        visible={seatsModal !== null}
        mode={seatsModal?.mode ?? "add"}
        subInfo={
          seatsModal?.subId
            ? otherSubs.find(s => s.subscription_details?.subscription_id === seatsModal.subId)
            : undefined
        }
        loading={
          Boolean(seatsModal?.subId) &&
          !otherSubs.find(s => s.subscription_details?.subscription_id === seatsModal?.subId)
        }
        onClose={() => setSeatsModal(null)}
      />
      {/* Buy seats + assign in one purchase (reuses the proven old-page flow, prorated price included) */}
      {addUsersInitial !== null && (
        <AddUserToPlanModal
          open={addUsersInitial !== null}
          initialUsersToAdd={addUsersInitial}
          onInvitedCb={() => undefined}
          onClose={() => setAddUsersInitial(null)}
        />
      )}
    </Page>
  );
};
